import { BigNumber } from "bignumber.js";

/**
 * Cleans numeric string by removing thousand separators
 * @param amount - The amount to clean (string or number)
 */
export function cleanAmount(amount: string | number): string {
  if (typeof amount === "number") return amount.toString();
  return amount.replace(/,/g, "");
}

/**
 * Convert token amount to USD
 * @param {string|number} tokenAmount - The amount of tokens
 * @param {string|number} tokenPrice - The price of one token in USD
 * @returns {BigNumber} - The equivalent amount in USD
 */
export function convertTokenAmountToUSD(
  tokenAmount: string | number,
  tokenPrice: string | number,
  maxDecimals: number = 4
): string {
  if (!tokenAmount || !tokenPrice) return "";
  try {
    const amount = new BigNumber(cleanAmount(tokenAmount));
    const price = new BigNumber(cleanAmount(tokenPrice));
    return amount.multipliedBy(price).decimalPlaces(maxDecimals).toString();
  } catch {
    return "";
  }
}

/**
 * Convert USD to token amount
 * @param {string|number} usdAmount - The amount in USD
 * @param {string|number} tokenPrice - The price of one token in USD
 * @param {number} maxDecimals - The maximum number of decimals
 * @returns {BigNumber} - The equivalent amount of tokens
 */
export function convertUSDToTokenAmount(
  usdAmount: string | number,
  tokenPrice: string | number,
  maxDecimals: number
): string {
  if (!usdAmount || !tokenPrice || !maxDecimals) return "0";
  const amount = new BigNumber(cleanAmount(usdAmount));
  const price = new BigNumber(cleanAmount(tokenPrice));

  return amount.dividedBy(price).decimalPlaces(maxDecimals).toString();
}

interface TokenWithBalance {
  balance: string;
  usdPrice: number;
  price24hChange: number;
}

export const calculateTotal24hChange = (
  tokensWithBalance: TokenWithBalance[],
  totalUSDAmount: number | string
): number => {
  let weightedChangeSum = 0;

  for (const token of tokensWithBalance) {
    const tokenBalance = parseFloat(token.balance);
    const tokenValueUSD = tokenBalance * token.usdPrice;
    const weightedChange =
      (tokenValueUSD / Number(totalUSDAmount)) * token.price24hChange;
    weightedChangeSum += weightedChange;
  }

  return weightedChangeSum;
};
